import { type Faro, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

let faro: Faro | undefined;

export interface TelemetryConfig {
  /** Faro collect endpoint. Empty disables telemetry entirely. */
  url: string;
  /** Public by nature: it ships in the bundle. */
  apiKey?: string;
  /** Identifies the sending application; the gateway promotes it to `service_name`. */
  appName: string;
  version: string;
  environment: string;
}

/**
 * Browser telemetry: errors, console output, web vitals, user interactions, and traces for
 * outgoing requests. Sends to the ingest gateway, or to the local Alloy in development.
 *
 * Takes its configuration rather than reading `window._env_`, which only the applications
 * declare. Does nothing when `url` is empty, so an environment that has not been wired up stays
 * silent instead of erroring on every batch.
 */
export const initTelemetry = ({ url, apiKey, appName, version, environment }: TelemetryConfig): void => {
  if (!url) {
    return;
  }

  faro = initializeFaro({
    url,
    apiKey: apiKey || undefined,
    app: { name: appName, version, environment },
    // After standby the first request can leave with the expired token while the refresh is
    // still in flight. The next one succeeds, so the 401 is noise, not a fault.
    ignoreErrors: ['Received status code 401'],
    instrumentations: [
      ...getWebInstrumentations({ captureConsole: true }),
      // Instruments fetch and XHR. The API is same-origin, so the traceparent header rides along
      // without CORS configuration, and the trace continues into the backend once that is
      // instrumented.
      new TracingInstrumentation(),
    ],
  });
};

/** Identifies the signed-in user on every subsequent event. */
export const setTelemetryUser = (user: { id?: string; username?: string; email?: string }): void => {
  faro?.api.setUser(user);
};

/** Records an error that was handled rather than thrown. */
export const reportError = (error: Error, context?: Record<string, string>): void => {
  faro?.api.pushError(error, { context });
};

/**
 * Records something worth knowing that is not a fault. Deliberately an event rather than an
 * error: the alert rules count `kind = exception`, and diagnostics must not page anyone.
 */
export const reportEvent = (name: string, attributes?: Record<string, string>): void => {
  faro?.api.pushEvent(name, attributes);
};

/**
 * For an error boundary's `onError`. Without it a render crash reaches telemetry only as the
 * string React hands to console.error, which captureConsole picks up — this keeps the Error
 * with its stack and adds the component stack, which that line does not carry.
 *
 * The value is `unknown` because a component may throw anything, not just an Error.
 */
export const reportRenderError = (error: unknown, info: { componentStack?: string | null }): void => {
  reportError(error instanceof Error ? error : new Error(String(error)), {
    componentStack: info.componentStack ?? '',
  });
};

/** Marks telemetry as coming from the desktop client rather than a browser. */
export const setDesktopVersion = (version: string): void => {
  faro?.api.setSession({ attributes: { desktopVersion: version } });
};
